import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MapPin, Play, ChevronLeft, ChevronRight, Sparkles, Clock, Award, Phone, CheckCircle, ArrowRight, Mountain, Waves, Heart, Flower2, Building, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { AnimatedSection, AnimatedCard, AnimatedCounter } from '../components/AnimatedComponents';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Theme icons mapping
const themeIcons = {
  beach: Waves,
  spiritual: Sparkles,
  cultural: Building,
  hill_stations: Mountain,
  ayurveda: Flower2
};

const HomePage = () => {
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(0);
  const [packages, setPackages] = useState([]);
  const [regionCounts, setRegionCounts] = useState({ south: 0, north: 0, east: 0, west: 0 });
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const carouselRef = useRef(null);

  // Get homepage content from settings
  const homeContent = settings?.home_page || {};
  const heroThemes = homeContent.hero_themes || [
    { id: 'beach', title: 'Beach Escapes', subtitle: 'Sun, Sand & Serenity', media_url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920', media_type: 'image' },
    { id: 'spiritual', title: 'Spiritual Journeys', subtitle: 'Find Your Inner Peace', media_url: 'https://images.unsplash.com/photo-1545126178-862cdb469409?w=1920', media_type: 'image' },
    { id: 'cultural', title: 'Cultural Heritage', subtitle: 'Discover Ancient Wonders', media_url: 'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=1920', media_type: 'image' },
    { id: 'hill_stations', title: 'Hill Stations', subtitle: 'Cool Mountain Retreats', media_url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920', media_type: 'image' },
    { id: 'ayurveda', title: 'Ayurveda & Wellness', subtitle: 'Rejuvenate Body & Soul', media_url: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1920', media_type: 'image' }
  ];
  const searchPlaceholder = homeContent.search_placeholder || "Where are you going?";
  const autoSuggestions = homeContent.auto_suggestions || ['Adventure', 'Heritage', 'Honeymoon', 'Family', 'Beach', 'Spiritual', 'Wildlife'];
  const whySentora = homeContent.why_sentora || [
    { title: '40+ Years of Excellence', description: 'Trusted since 1985 with lakhs of happy travelers' },
    { title: 'Expert Local Guides', description: 'Handpicked professionals who know every corner' },
    { title: 'Complete Packages', description: 'Hotels, transport, meals - everything included' },
    { title: 'Personalized Service', description: 'Tailored experiences for your unique journey' }
  ];
  const regionTiles = homeContent.region_tiles || [
    { id: 'south', name: 'South India', image: 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=800' },
    { id: 'north', name: 'North India', image: 'https://images.unsplash.com/photo-1564507592333-c60657eea523?w=800' },
    { id: 'east', name: 'East India', image: 'https://images.unsplash.com/photo-1558431382-27e303142255?w=800' },
    { id: 'west', name: 'West India', image: 'https://images.unsplash.com/photo-1567157577867-05ccb1388e66?w=800' }
  ];

  // Fetch packages and calculate region counts
  useEffect(() => {
    fetch(`${API_URL}/api/packages`)
      .then(res => res.json())
      .then(data => {
        setPackages(data);
        // Calculate region counts
        const counts = { south: 0, north: 0, east: 0, west: 0 };
        data.forEach(pkg => {
          const pkgRegions = Array.isArray(pkg.region) ? pkg.region : [pkg.region];
          pkgRegions.forEach(r => {
            if (counts.hasOwnProperty(r)) {
              counts[r]++;
            }
          });
        });
        setRegionCounts(counts);
      })
      .catch(() => {});
  }, []);

  // Get unique destinations from packages grouped by region
  // featuredOnly: if true, only include packages with is_featured === true
  const getDestinationsByRegion = (featuredOnly = false) => {
    const regionDestinations = { south: [], north: [], east: [], west: [] };
    const filteredPackages = featuredOnly 
      ? packages.filter(pkg => pkg.is_featured === true)
      : packages;
    
    filteredPackages.forEach(pkg => {
      const pkgRegions = Array.isArray(pkg.region) ? pkg.region : [pkg.region];
      pkgRegions.forEach(region => {
        if (regionDestinations[region] && pkg.destination) {
          const existing = regionDestinations[region].find(d => d.name.toLowerCase() === pkg.destination.toLowerCase());
          if (!existing) {
            regionDestinations[region].push({
              name: pkg.destination,
              image: pkg.image,
              count: 1
            });
          } else {
            existing.count++;
          }
        }
      });
    });
    return regionDestinations;
  };

  // Auto-rotate hero themes
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTheme((prev) => (prev + 1) % heroThemes.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [heroThemes.length]);

  // Handle search with auto-suggestions - show available destinations
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setShowSuggestions(value.length > 0 || document.activeElement?.id === 'hero-search');
  };

  // Filter destinations based on search
  // When searchQuery is empty: show only featured packages
  // When searchQuery has text: search ALL packages
  const getFilteredDestinations = () => {
    const query = searchQuery.toLowerCase().trim();
    const isTyping = query.length > 0;
    
    // Get destinations: featured only when not typing, all when typing
    const destinationsByRegion = getDestinationsByRegion(!isTyping);
    
    const filtered = { south: [], north: [], east: [], west: [] };
    Object.keys(destinationsByRegion).forEach(region => {
      filtered[region] = destinationsByRegion[region].filter(d => 
        !query || d.name.toLowerCase().includes(query)
      );
    });
    return filtered;
  };

  const filteredDestinations = getFilteredDestinations();
  const hasAnyDestinations = Object.values(filteredDestinations).some(arr => arr.length > 0);

  const handleSearch = (query) => {
    if (query) {
      // Redirect to packages page with search query
      navigate(`/packages?q=${encodeURIComponent(query)}`);
    }
    setShowSuggestions(false);
  };

  const handleDestinationClick = (destination) => {
    navigate(`/packages?q=${encodeURIComponent(destination)}`);
    setShowSuggestions(false);
  };

  const handleThemeClick = (themeId) => {
    navigate(`/packages?theme=${themeId}`);
  };

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: direction === 'left' ? -350 : 350, behavior: 'smooth' });
    }
  };

  const currentHero = heroThemes[currentTheme];
  const ThemeIcon = themeIcons[currentHero?.id] || Sparkles;

  return (
    <div className="bg-s-bg min-h-screen">
      <SEO 
        title="Sentora Holidays - Experience Creators Since 1985 | India Tour Packages" 
        description="India's trusted travel partner since 1985. Explore 50+ curated tour packages across Kerala, Rajasthan, Goa, Kashmir, and more. Book your dream vacation today!"
        keywords="India tours, Kerala packages, Rajasthan tours, Goa holidays, travel agency India, holiday packages, tour operators, beach holidays, spiritual tours"
        url="https://www.sentoraholidays.com"
      />

      {/* Hero Section - Theme Based Carousel */}
      <section className="relative h-[100svh] md:h-[90vh]" data-testid="hero-section">
        {/* Background Media - with overflow hidden */}
        <div className="absolute inset-0 overflow-hidden">
          {heroThemes.map((theme, idx) => (
            <div
              key={theme.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${idx === currentTheme ? 'opacity-100' : 'opacity-0'}`}
            >
              {theme.media_type === 'video' ? (
                <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                  <source src={theme.media_url} type="video/mp4" />
                </video>
              ) : (
                <img loading="lazy" src={theme.media_url} alt={theme.title} className="w-full h-full object-cover" />
              )}
            </div>
          ))}
        </div>

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30" />

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-4">
          {/* Theme Badge */}
          <div className="mb-6 flex items-center gap-2">
            <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 px-4 py-2">
              <ThemeIcon size={16} className="mr-2" />
              {currentHero?.title}
            </Badge>
          </div>

          {/* Main Title */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white text-center mb-4 font-heritage">
            {currentHero?.subtitle}
          </h1>

          {/* Search Bar - Overlaid on Hero */}
          <div className="w-full max-w-2xl mt-8 relative">
            <div className="bg-s-card rounded-full shadow-2xl p-2 flex items-center border border-s-border">
              <div className="flex-1 flex items-center gap-3 px-4 lg:px-6">
                <MapPin className="text-s-primary" size={24} />
                <div className="flex-1 relative">
                  <p className="text-xs text-s-muted font-medium">{searchPlaceholder}</p>
                  <input
                    id="hero-search"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 300)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
                    className="text-s-text font-medium outline-none w-full text-base bg-transparent"
                    placeholder="Try: Kerala, Rajasthan, Goa..."
                    data-testid="hero-search-input"
                  />
                </div>
              </div>
              <Button 
                onClick={() => handleSearch(searchQuery)}
                className="bg-s-primary hover:bg-s-primary-h rounded-full h-12 w-12 lg:h-14 lg:w-40 p-0"
                data-testid="hero-search-btn"
              >
                <Search className="text-white lg:mr-2" size={20} />
                <span className="hidden lg:inline text-white font-semibold">Search</span>
              </Button>
            </div>

            {/* Destination Suggestions Popup - Grouped by Region */}
            {showSuggestions && hasAnyDestinations && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-s-card rounded-2xl shadow-2xl overflow-hidden z-50 border border-s-border max-h-[70vh] overflow-y-auto">
                <div className="p-4">
                  {searchQuery ? (
                    <p className="text-xs text-s-muted mb-3 flex items-center gap-2">
                      <Search size={14} /> Searching all packages for "{searchQuery}"
                    </p>
                  ) : (
                    <p className="text-xs text-s-muted mb-3 flex items-center gap-2">
                      <Award size={14} className="text-s-primary" /> Traveller's Favourites — Type to search all
                    </p>
                  )}
                  
                  {/* Region Sections */}
                  {[
                    { id: 'south', name: 'South India', color: 'bg-s-primary' },
                    { id: 'north', name: 'North India', color: 'bg-s-primary/80' },
                    { id: 'east', name: 'East India', color: 'bg-s-primary/90' },
                    { id: 'west', name: 'West India', color: 'bg-s-primary/70' }
                  ].map(region => (
                    filteredDestinations[region.id]?.length > 0 && (
                      <div key={region.id} className="mb-4 last:mb-0">
                        <div className="flex items-center gap-2 mb-2 px-2">
                          <div className={`w-2 h-2 rounded-full ${region.color}`} />
                          <h4 className="text-xs font-semibold text-s-text uppercase tracking-wider">{region.name}</h4>
                          <span className="text-xs text-s-muted">({filteredDestinations[region.id].length})</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {filteredDestinations[region.id].slice(0, 6).map((dest, idx) => (
                            <button
                              key={idx}
                              onMouseDown={() => handleDestinationClick(dest.name)}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-s-bg2 transition-all text-left group"
                            >
                              <img 
                                src={dest.image} 
                                alt={dest.name} 
                                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-s-text truncate group-hover:text-s-primary transition-colors">{dest.name}</p>
                                <p className="text-xs text-s-primary">View Packages</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                  
                  {/* No Results */}
                  {!hasAnyDestinations && searchQuery && (
                    <div className="text-center py-4">
                      <p className="text-s-muted text-sm">No destinations found for "{searchQuery}"</p>
                      <Button 
                        onClick={() => navigate(`/ai-planner?destination=${encodeURIComponent(searchQuery)}`)}
                        className="mt-2 bg-s-primary text-white"
                        size="sm"
                      >
                        Plan with AI
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Theme Pills - Clickable */}
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {heroThemes.map((theme, idx) => {
              const Icon = themeIcons[theme.id] || Sparkles;
              return (
                <button
                  key={theme.id}
                  onClick={() => handleThemeClick(theme.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                    idx === currentTheme 
                      ? 'bg-s-card text-s-primary border border-s-border' 
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  data-testid={`theme-pill-${theme.id}`}
                >
                  <Icon size={16} />
                  <span className="text-sm font-medium">{theme.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Theme Navigation Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {heroThemes.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentTheme(idx)}
              className={`h-2 rounded-full transition-all ${idx === currentTheme ? 'w-8 bg-white' : 'w-2 bg-white/50'}`}
            />
          ))}
        </div>
      </section>

      {/* Explore by Destination - Region Tiles */}
      <section className="py-16 md:py-24 px-4 bg-s-bg">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <Badge className="bg-s-accent/20 text-s-accent border-s-accent/30 mb-4">Explore India</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-s-text mb-4 font-heritage italic">
              Explore by Destination
            </h2>
            <p className="text-s-text2 max-w-2xl mx-auto">
              Discover the diverse beauty of India across four unique regions
            </p>
          </AnimatedSection>

          {/* 4 Region Tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {regionTiles.map((region, idx) => (
              <AnimatedCard
                key={region.id}
                delay={idx * 100}
                hoverEffect="none"
                className="group"
              >
                <Link
                  to={`/packages?region=${region.id}`}
                  className="block relative"
                  onMouseEnter={() => setHoveredRegion(region.id)}
                  onMouseLeave={() => setHoveredRegion(null)}
                  data-testid={`region-tile-${region.id}`}
                >
                  <div className="relative h-48 md:h-64 rounded-2xl overflow-hidden hover-zoom">
                    <img
                      src={region.image}
                      alt={region.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    
                    {/* Tour count on hover */}
                    <div className={`absolute inset-0 bg-s-primary/80 flex items-center justify-center transition-opacity duration-300 ${hoveredRegion === region.id ? 'opacity-100' : 'opacity-0'}`}>
                      <div className="text-center">
                        <p className="text-4xl md:text-5xl font-bold text-white">{regionCounts[region.id]}</p>
                        <p className="text-white/80 text-sm">Tours Available</p>
                      </div>
                    </div>

                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl md:text-2xl font-bold text-white mb-1">{region.name}</h3>
                      <div className="flex items-center gap-2 text-white/80 text-sm group-hover:text-white transition-colors">
                        <span>Explore {region.name.split(' ')[0]}</span>
                        <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              </AnimatedCard>
            ))}
          </div>

          {/* View All Packages CTA */}
          <AnimatedSection delay={400} className="text-center mt-10">
            <Link to="/packages">
              <Button size="lg" className="bg-s-primary hover:bg-s-primary-h text-white px-8 h-14 rounded-full btn-animate">
                <Play className="mr-2" size={18} />
                View All Packages
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Why Sentora Holidays */}
      <section className="py-16 md:py-24 px-4 bg-s-bg2">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <Badge className="bg-s-primary/20 text-s-primary border-s-primary/30 mb-4">Why Choose Us</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-s-text mb-4 font-heritage italic">
              Why Sentora Holidays
            </h2>
            <p className="text-s-text2 max-w-2xl mx-auto">
              Four decades of crafting unforgettable travel experiences
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whySentora.map((item, idx) => (
              <AnimatedCard key={idx} delay={idx * 100} hoverEffect="lift-subtle" className="bg-s-card border border-s-border hover:border-s-primary/30 rounded-xl">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-s-primary rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-s-text font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-s-text2 text-sm">{item.description}</p>
                </CardContent>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Packages Carousel */}
      {packages.length > 0 && (
        <section className="py-16 md:py-24 px-4 bg-s-bg">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection className="text-center mb-12">
              <Badge className="bg-s-primary/20 text-s-primary border-s-primary/30 mb-4">
                <Award size={14} className="mr-1" /> Featured
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-s-text mb-4 font-heritage italic">
                Popular Tour Packages
              </h2>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="relative group">
                <button
                  onClick={() => scrollCarousel('left')}
                  className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/80 hover:bg-black p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity -translate-x-4"
                >
                  <ChevronLeft className="text-white" size={24} />
                </button>
                <button
                  onClick={() => scrollCarousel('right')}
                  className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/80 hover:bg-black p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity translate-x-4"
                >
                  <ChevronRight className="text-white" size={24} />
                </button>

                <div
                  ref={carouselRef}
                  className="flex gap-4 md:gap-6 overflow-x-auto pb-4 scroll-smooth snap-x snap-mandatory"
                  style={{ scrollbarWidth: 'none' }}
                >
                  {packages.slice(0, 10).map((pkg) => (
                    <Link
                      key={pkg.id}
                      to={`/packages/${pkg.id}`}
                    className="flex-shrink-0 w-[300px] md:w-[340px] snap-start"
                    data-testid={`package-card-${pkg.id}`}
                  >
                    <Card className="bg-s-card border-s-border overflow-hidden hover:shadow-xl transition-all group">
                      <div className="relative h-48">
                        <img loading="lazy" src={pkg.image} alt={pkg.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute top-3 left-3 flex flex-wrap gap-1">
                          {(Array.isArray(pkg.theme) ? pkg.theme.slice(0, 2) : [pkg.theme]).map((t, i) => (
                            <Badge key={i} className="bg-s-primary text-white text-xs">
                              {t?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </Badge>
                          ))}
                        </div>
                        <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-sm font-bold">
                          {pkg.duration_nights}N / {pkg.duration_days}D
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <h3 className="text-s-text font-bold text-lg mb-2 line-clamp-1">{pkg.title}</h3>
                        <p className="text-s-text2 text-sm mb-2 line-clamp-1">
                          {pkg.cities?.join(' • ') || pkg.destination}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 text-s-text2 text-sm">
                            <Clock size={14} />
                            {pkg.duration_days ?? 4}D / {pkg.duration_nights ?? 3}N
                          </div>
                          <span className="text-s-primary text-sm font-semibold flex items-center gap-1">
                            View Details <ArrowRight size={14} />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {(settings?.testimonials || []).length > 0 && (
        <section className="py-16 md:py-24 px-4 bg-s-bg2">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection className="text-center mb-12">
              <Badge className="bg-s-primary/20 text-s-primary border-s-primary/30 mb-4">
                <Heart size={14} className="mr-1" /> Traveler Stories
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-s-text mb-4 font-heritage italic">
                What Our Guests Say
              </h2>
              <p className="text-s-text2 max-w-2xl mx-auto">
                Real experiences from travelers who explored with Sentora Holidays
              </p>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="relative group">
                <button
                  onClick={() => {
                    const container = document.getElementById('testimonials-carousel');
                    if (container) container.scrollBy({ left: -400, behavior: 'smooth' });
                  }}
                  className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/80 hover:bg-black p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity -translate-x-4"
                >
                  <ChevronLeft className="text-white" size={24} />
                </button>
                <button
                  onClick={() => {
                    const container = document.getElementById('testimonials-carousel');
                    if (container) container.scrollBy({ left: 400, behavior: 'smooth' });
                  }}
                  className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/80 hover:bg-black p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity translate-x-4"
                >
                  <ChevronRight className="text-white" size={24} />
                </button>

                <div
                  id="testimonials-carousel"
                  className="flex gap-4 md:gap-6 overflow-x-auto pb-4 scroll-smooth snap-x snap-mandatory"
                  style={{ scrollbarWidth: 'none' }}
                >
                  {(settings?.testimonials || []).map((testimonial, idx) => (
                    <div key={idx} className="flex-shrink-0 w-[320px] md:w-[380px] snap-start">
                      <Card className="bg-s-card border-s-border h-full hover-lift-subtle transition-all">
                        <CardContent className="p-6">
                          <Quote className="w-8 h-8 text-s-primary/30 mb-4" />
                          <p className="text-s-text2 text-sm md:text-base mb-6 italic line-clamp-4">
                            "{testimonial.content}"
                          </p>
                          <div className="flex items-center gap-4 pt-4 border-t border-s-border">
                            {testimonial.image ? (
                              <img loading="lazy" src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-s-primary/20 flex items-center justify-center text-s-primary font-bold">
                              {testimonial.name?.charAt(0) || 'T'}
                            </div>
                          )}
                          <div>
                            <p className="text-s-text font-semibold">{testimonial.name}</p>
                            <p className="text-s-text2 text-sm">{testimonial.destination || testimonial.role}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-28 px-4 bg-s-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0l20 20-20 20L0 20z' fill='%23ffffff' fill-opacity='0.1'/%3E%3C/svg%3E\")" }} />
        <AnimatedSection className="max-w-4xl mx-auto text-center relative z-10">
          <Badge className="bg-white/20 text-white border-white/30 mb-6 badge-pop">Start Your Journey</Badge>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-heritage italic">
            Ready for Your Next Adventure?
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Let our experts craft the perfect itinerary for you. 40 years of experience at your service.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/ai-planner">
              <Button size="lg" className="w-full sm:w-auto bg-white text-s-primary hover:bg-gray-100 px-8 h-14 font-bold rounded-full btn-animate">
                <Sparkles className="mr-2" size={18} />
                Plan with AI
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10 px-8 h-14 rounded-full btn-animate">
                <Phone className="mr-2" size={18} />
                Contact Us
              </Button>
            </Link>
          </div>
        </AnimatedSection>
      </section>
    </div>
  );
};

export default HomePage;
